import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import { useCallback, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import noop from 'lib/function/noop'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import useAgentHubAddressAutocomplete from 'agentHub/hooks/useAgentHubAddressAutocomplete'
import type { GooglePlaceDetails } from 'api/search'
import { getGooglePlaceDetails } from 'api/search'

interface Props {
  onSelect?: (option: GooglePlaceDetails) => void
  selectedPlaceId?: string
  className?: string
  inputName?: string
  label: string
  placeholder?: string
}

const StyledGroup = styled(Group)`
  padding: ${rem(8)};
  padding-top: 0;
`

function getAddressValue(place: Array<any>, type: string) {
  const addressValue = place.find((component) => {
    return component.types.includes(type)
  })?.longName || ''

  return addressValue
}

function AgentHubAddressAutocomplete(props: Props) {
  const {
    className,
    onSelect = noop,
    selectedPlaceId,
    inputName,
    label,
    placeholder,
  } = props

  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchPhrase, setSearchPhrase] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)
  const canRenderDropdown = searchPhrase.length >= 1

  const { isLoading, predictions } = useAgentHubAddressAutocomplete(searchPhrase)

  const noResultsFound = !isLoading && predictions.length === 0 && searchPhrase?.length > 1

  const onItemSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const placeId = e.currentTarget.dataset.placeid
    if (placeId) {
      getGooglePlaceDetails(placeId).then((result) => {
        onSelect(result)
        const street = getAddressValue(result, 'route')
        const streetNumber = getAddressValue(result, 'street_number')
        setInputValue(`${streetNumber} ${street}`)
      })
    }
    setIsFocused(false)
  }, [onSelect])

  const handleOnFocus = () => {
    setSearchPhrase('')
    setIsFocused(true)
  }

  const handleClickOutside = () => {
    setIsFocused(false)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.currentTarget.value)
    setSearchPhrase(event.currentTarget.value)
  }

  return <VerticalSpacer gap={12} className={className}>
    <TextInput
      name={inputName}
      value={inputValue}
      label={label}
      autoComplete="off"
      onChange={handleInputChange}
      endIcon={<LineSearchIcon />}
      defaultValue={searchPhrase}
      ref={inputRef}
      placeholder={placeholder}
      noValidationSpacing
      onFocus={handleOnFocus}
      required
    />
    {inputRef?.current && (
      <DropdownList
        size="fill-anchor"
        open={isFocused && canRenderDropdown}
        anchorRef={inputRef}
        triggerRef={inputRef}
        onClose={handleClickOutside}
      >
        {!isLoading && predictions.length > 0 && <div>
          {predictions.map(option => (
            <ClickableListItem
              key={option.placeId}
              selected={option.placeId === selectedPlaceId}
              data-placeid={option.placeId}
              onClick={onItemSelect}
              size="medium"
              title={option.mainText}
              subtitle={option.secondaryText}
              startIcon={<LineMapMarkerIcon />}
            />
          ))}
        </div>}

        {isLoading && <LoadingIndicator inline />}

        {!isLoading && noResultsFound && (
          <StyledGroup direction="vertical" gap={4}>
            <BodyText variant="medium" colour="neutral-two">
              <FormattedMessage
                id="DYGzJmCGPP5rGjvuWnbP"
                defaultMessage="Sorry, we couldn’t find results for <strong>{searchPhrase}</strong>."
                description="Address autocomplete no results message in the search and discovery interface"
                values={{
                  searchPhrase,
                  strong: (chunks: React.ReactNode) => (
                    <BodyText
                      colour="neutral-one"
                      as="span"
                      variant="medium"
                      weight="bold"
                    >
                      {chunks}
                    </BodyText>
                  ),
                }}
              />
            </BodyText>
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage({
                id: 'xvTmkHlzpm9CUQY6_PlG',
                defaultMessage: 'Try a different search phrase',
                description: 'Address autocomplete no results help text in the search interface',
              })}
            </BodyText>
          </StyledGroup>
        )}

      </DropdownList>
    )}
  </VerticalSpacer>
}

export default AgentHubAddressAutocomplete
